// vehicles.js — register vozidiel + TEMPORÁLNY DPH režim (50/100) knihy jázd.
// Spec: MasterProject/feature-ideas/ideas/todue-kniha-jazd-SPEC-v2.md §1–§2.
// Právne pravidlá: PORADCA-ODPOVEDE.md (usmernenie FS 3/DPH/2025/MU) + tmem #66/#67/#68.
//
// PREČO TEMPORÁLNE: režim vozidla (50 % paušál vs 100 % výlučne firemné) je STAV
// s časovou platnosťou — mení sa v čase (kúpa → oznámenie FS → prípadný prechod na
// zmiešané). Jazda z 5.1.2026 sa posudzuje režimom platným 5.1.2026, nie dnešným.
// Preto `intervals[]` (valid_from/valid_to), nie statické pole na vozidle.
//
// Čistá logika (regimeAt / validateIntervals / checkRegime100Eligibility) je bez DB a
// bez Reactu → plne testovateľná. DB wrappery (createVehicle / setRegimeInterval) sú tenké.
import { db, newCommonFields } from './db';

// ─── Právne konštanty (podľa poradcu — usmernenie FS 3/DPH/2025/MU) ───────────
// 100 % odpočet DPH: len M1/L1e/L3e, výlučne firemné + oznámenie FS, v okne nižšie.
export const REGIME_100_CATEGORIES = ['M1', 'L1e', 'L3e'];
// Okno nároku na 100 %: obstaranie/použitie 1.1.2026 – 30.6.2028 (konsolidačný zákon).
export const REGIME_100_WINDOW = { from: '2026-01-01', to: '2028-06-30' };
// Náhrada za km od 1.1.2026 (oznámenie 340/2025, tmem #66) — 0,313 €/km, nie 0,296.
export const NAHRADA_EUR_PER_KM_2026 = 0.313;

export const DPH_REZIMY = [50, 100];
export const USAGE_TYPES = ['vylucne_firemne', 'zmiesane'];
export const PHM_REZIMY = ['pausal80', 'skutocne', 'nahrady'];

// ─── Date-only normalizácia — režim sa mení na hranici DŇA, nie sekundy ───────
// ISO 'YYYY-MM-DD…' → 'YYYY-MM-DD'. Lexikografické porovnanie date-only stringov je
// spoľahlivé (žiadny tz drift). Vstup bez validného tvaru ALEBO nemožný kalendárny
// dátum (2026-02-31) → null (Codex #4: samotný regex tvar nestačí — 31.2. by prešlo
// a skreslilo temporálne hranice/okno nároku).
export function dateOnly(iso) {
  if (typeof iso !== 'string') return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const yy = Number(y);
  const mm = Number(mo);
  const dd = Number(d);
  // Reálny kalendárny dátom: Date.UTC normalizuje pretečenie (31.2.→3.3.) — ak sa
  // komponenty po spätnom čítaní nezhodujú, vstup bol nemožný dátum.
  const dt = new Date(Date.UTC(yy, mm - 1, dd));
  if (dt.getUTCFullYear() !== yy || dt.getUTCMonth() !== mm - 1 || dt.getUTCDate() !== dd) return null;
  return `${y}-${mo}-${d}`;
}

// ─── Interval normalizácia — whitelist polí (žiadny cudzí kľúč do stavu) ───────
const INTERVAL_FIELDS = [
  'valid_from', 'valid_to', 'dph_rezim', 'usage', 'phm_rezim',
  'oznamenie_fs', 'datum_oznamenia',
];
function normalizeInterval(raw) {
  const out = {};
  for (const k of INTERVAL_FIELDS) out[k] = raw && raw[k] !== undefined ? raw[k] : null;
  out.valid_from = dateOnly(out.valid_from);
  out.valid_to = out.valid_to == null ? null : dateOnly(out.valid_to);
  out.datum_oznamenia = out.datum_oznamenia == null ? null : dateOnly(out.datum_oznamenia);
  out.dph_rezim = out.dph_rezim == null ? null : Number(out.dph_rezim);
  out.oznamenie_fs = out.oznamenie_fs === true;
  return out;
}

/**
 * validateIntervals(intervals, { acquiredAt }) — invarianty temporálnej rady:
 *   • valid_from platný date; ak valid_to nie je null, valid_from < valid_to,
 *   • dph_rezim ∈ {50,100}, usage/phm_rezim z enumu,
 *   • zoradené vzostupne, ŽIADNE prekryvy (dva režimy v ten istý deň = nejednoznačné),
 *   • ŽIADNE medzery (kontinuita) — koniec jedného = začiatok ďalšieho; deň bez
 *     režimu = jazda bez posudzovateľného stavu = dôkazná diera,
 *   • iba POSLEDNÝ interval smie mať valid_to = null (otvorený „doteraz"),
 *   • prvý valid_from = acquired_at (ak zadané) — režim day-1 od obstarania.
 * Vracia { ok, errors[] }. Nemení vstup.
 */
export function validateIntervals(intervals, { acquiredAt } = {}) {
  const errors = [];
  const list = (Array.isArray(intervals) ? intervals : []).map(normalizeInterval);
  if (list.length === 0) return { ok: true, errors }; // prázdna rada je validná (vozidlo bez režimu zatiaľ)

  const sorted = [...list].sort((a, b) => (a.valid_from < b.valid_from ? -1 : a.valid_from > b.valid_from ? 1 : 0));

  for (let i = 0; i < sorted.length; i += 1) {
    const iv = sorted[i];
    if (!iv.valid_from) { errors.push(`interval[${i}]: chýba/zlý valid_from`); continue; }
    if (!DPH_REZIMY.includes(iv.dph_rezim)) errors.push(`interval[${i}]: dph_rezim musí byť 50 alebo 100 (dostal ${iv.dph_rezim})`);
    if (iv.usage && !USAGE_TYPES.includes(iv.usage)) errors.push(`interval[${i}]: usage neznámy (${iv.usage})`);
    if (iv.phm_rezim && !PHM_REZIMY.includes(iv.phm_rezim)) errors.push(`interval[${i}]: phm_rezim neznámy (${iv.phm_rezim})`);
    if (iv.valid_to != null && !(iv.valid_from < iv.valid_to)) {
      errors.push(`interval[${i}]: valid_from (${iv.valid_from}) musí byť pred valid_to (${iv.valid_to})`);
    }
    if (iv.valid_to == null && i !== sorted.length - 1) {
      errors.push(`interval[${i}]: len posledný interval smie byť otvorený (valid_to=null)`);
    }
    if (i > 0) {
      const prev = sorted[i - 1];
      if (prev.valid_to == null) {
        errors.push(`interval[${i}]: prekryv — predošlý interval je otvorený (valid_to=null) a nasleduje ďalší`);
      } else if (prev.valid_to > iv.valid_from) {
        errors.push(`interval[${i}]: prekryv s predošlým (predošlý končí ${prev.valid_to}, tento začína ${iv.valid_from})`);
      } else if (prev.valid_to < iv.valid_from) {
        errors.push(`interval[${i}]: medzera — predošlý končí ${prev.valid_to}, tento začína ${iv.valid_from} (deň bez režimu)`);
      }
    }
  }

  const acq = dateOnly(acquiredAt);
  if (acq && sorted[0].valid_from && sorted[0].valid_from !== acq) {
    errors.push(`prvý interval začína ${sorted[0].valid_from}, ale vozidlo obstarané ${acq} — režim musí platiť od obstarania (day-1)`);
  }
  return { ok: errors.length === 0, errors };
}

/**
 * regimeAt(vehicle, isoDate) — ktorý interval (režim) platil v daný deň. Interval
 * pokrýva [valid_from, valid_to) — začiatok inclusive, koniec exclusive (deň prechodu
 * patrí novému režimu). Otvorený interval (valid_to=null) pokrýva všetko od valid_from.
 * Vracia normalizovaný interval alebo null (mimo pokrytia).
 */
export function regimeAt(vehicle, isoDate) {
  const day = dateOnly(isoDate);
  if (!day) return null;
  const list = (Array.isArray(vehicle?.intervals) ? vehicle.intervals : []).map(normalizeInterval);
  for (const iv of list) {
    if (!iv.valid_from) continue;
    if (day < iv.valid_from) continue;
    if (iv.valid_to == null || day < iv.valid_to) return iv;
  }
  return null;
}

/**
 * checkRegime100Eligibility(vehicle, interval, { atDate }) — smie tento interval
 * niesť 100 %? Podľa poradcu 100 % vyžaduje SÚČASNE:
 *   • kategória M1/L1e/L3e,
 *   • usage=vylucne_firemne,
 *   • oznámenie FS (oznamenie_fs=true + datum_oznamenia),
 *   • valid_from v okne 1.1.2026–30.6.2028,
 *   • vozidlo obstarané v okne (acquired_at >= 1.1.2026 — pred oknom nie je nárok; Codex #2),
 *   • ak je zadaný atDate (dátum konkrétnej jazdy), aj tá jazda musí padnúť do okna —
 *     100 % nárok NEplatí po 30.6.2028, aj keď interval ostal otvorený (Codex #1).
 * Vracia { eligible, reasons[] }. (50 % je vždy eligible — default/paušál.)
 */
export function checkRegime100Eligibility(vehicle, interval, { atDate } = {}) {
  const iv = normalizeInterval(interval);
  if (iv.dph_rezim !== 100) return { eligible: true, reasons: [] };
  const reasons = [];
  const cat = vehicle?.category;
  if (!REGIME_100_CATEGORIES.includes(cat)) {
    reasons.push(`kategória ${cat ?? '—'} nie je M1/L1e/L3e (100 % sa nevzťahuje)`);
  }
  if (iv.usage !== 'vylucne_firemne') {
    reasons.push('usage musí byť vylucne_firemne (výlučne na podnikanie)');
  }
  const day = dateOnly(atDate);
  if (!iv.oznamenie_fs || !iv.datum_oznamenia) {
    reasons.push('chýba oznámenie správcovi dane (tlačivo OZNMVv26) — oznamenie_fs + datum_oznamenia');
  } else if (day && iv.datum_oznamenia > day) {
    // Oznámenie podané AŽ PO dátume jazdy/dokladu → v ten deň 100 % nárok ešte neexistoval
    // (Codex #2: samotná existencia datum_oznamenia nestačí, musí byť <= atDate).
    reasons.push(`oznámenie FS (${iv.datum_oznamenia}) je až po dátume ${day} — 100 % nárok pred oznámením neplatí`);
  }
  if (!iv.valid_from || iv.valid_from < REGIME_100_WINDOW.from || iv.valid_from > REGIME_100_WINDOW.to) {
    reasons.push(`valid_from ${iv.valid_from ?? '—'} je mimo okna nároku ${REGIME_100_WINDOW.from}–${REGIME_100_WINDOW.to}`);
  }
  const acq = dateOnly(vehicle?.acquired_at);
  if (!acq || acq < REGIME_100_WINDOW.from || acq > REGIME_100_WINDOW.to) {
    reasons.push(`vozidlo obstarané ${acq ?? '—'} — 100 % sa nevzťahuje na vozidlá mimo okna obstarania ${REGIME_100_WINDOW.from}–${REGIME_100_WINDOW.to}`);
  }
  if (day && (day < REGIME_100_WINDOW.from || day > REGIME_100_WINDOW.to)) {
    reasons.push(`jazda ${day} je mimo okna nároku na 100 % (${REGIME_100_WINDOW.from}–${REGIME_100_WINDOW.to})`);
  }
  return { eligible: reasons.length === 0, reasons };
}

// ─── DB: register vozidiel ────────────────────────────────────────────────────

/**
 * createVehicle(input) — nové vozidlo. intervals prázdne (režim sa nastaví
 * setRegimeInterval, aby prešiel 100 % guardom). Vracia uložený riadok.
 */
export async function createVehicle(input = {}) {
  if (!input.vin && !input.ecv) {
    throw new Error('[v2/vehicles] createVehicle vyžaduje aspoň vin alebo ecv (identita vozidla)');
  }
  const row = {
    ...newCommonFields(),
    vin: input.vin ?? null,
    ecv: input.ecv ?? null,
    label: input.label ?? null,
    category: input.category ?? null,
    fuel_type: input.fuel_type ?? null,
    norm_consumption: input.norm_consumption ?? null,
    acquired_at: dateOnly(input.acquired_at),
    odo_start: input.odo_start ?? null,
    odo_start_at: dateOnly(input.odo_start_at),
    intervals: [],
    custom: input.custom ?? null,
  };
  await db.vehicles.put(row);
  return row;
}

/**
 * setRegimeInterval(vehicleId, interval) — otvorí nový režimový interval od
 * `interval.valid_from`. Automaticky UZAVRIE predošlý otvorený interval (jeho
 * valid_to = valid_from nového — spojité, bez medzery/prekryvu). Pred zápisom:
 *   • 100 % interval MUSÍ prejsť checkRegime100Eligibility (inak throw — nezapíšeme
 *     nepodložený nárok na 100 %, to je presne dôkazná diera),
 *   • celá rada musí prejsť validateIntervals.
 * Vracia updated vehicle. LWW: rev+1, updated_at teraz.
 */
export async function setRegimeInterval(vehicleId, interval) {
  const iv = normalizeInterval(interval);
  if (!iv.valid_from) throw new Error('[v2/vehicles] interval vyžaduje valid_from (deň odkedy režim platí)');
  if (!DPH_REZIMY.includes(iv.dph_rezim)) {
    throw new Error(`[v2/vehicles] dph_rezim musí byť 50 alebo 100, dostal ${JSON.stringify(interval?.dph_rezim)}`);
  }

  // Read-modify-write ATOMICKY (Codex #3): bez tx by dva súbežné setRegimeInterval
  // čítali ten istý východiskový stav a druhý put by prepísal prvý → jeden režimový
  // prechod ticho zmizne. Tx serializuje (engine mutex), re-read je vnútri.
  return db.transaction('rw', db.vehicles, async (tx) => {
    const vehicle = await tx.vehicles.get(vehicleId);
    if (!vehicle) throw new Error(`[v2/vehicles] vozidlo ${JSON.stringify(vehicleId)} neexistuje`);

    const elig = checkRegime100Eligibility(vehicle, iv);
    if (!elig.eligible) {
      throw new Error(`[v2/vehicles] 100 % režim nespĺňa podmienky: ${elig.reasons.join('; ')}`);
    }

    const existing = (Array.isArray(vehicle.intervals) ? vehicle.intervals : []).map(normalizeInterval);
    // Uzavri predošlý otvorený interval na hranici nového (spojito). Ak nový začína
    // pred/na začiatku existujúceho otvoreného → chyba (nedá sa vsunúť do minulosti).
    const next = [];
    for (const prev of existing) {
      if (prev.valid_to == null) {
        if (!(prev.valid_from < iv.valid_from)) {
          throw new Error(`[v2/vehicles] nový interval (${iv.valid_from}) musí začať PO otvorenom predošlom (${prev.valid_from})`);
        }
        next.push({ ...prev, valid_to: iv.valid_from });
      } else {
        next.push(prev);
      }
    }
    next.push({ ...iv, valid_to: null }); // nový interval je otvorený

    const check = validateIntervals(next, { acquiredAt: vehicle.acquired_at });
    if (!check.ok) throw new Error(`[v2/vehicles] neplatná temporálna rada: ${check.errors.join('; ')}`);

    const updated = { ...vehicle, intervals: next, rev: (vehicle.rev || 1) + 1, updated_at: new Date().toISOString() };
    await tx.vehicles.put(updated);
    return updated;
  });
}

// listVehicles() — register (bez soft-deleted).
export async function listVehicles() {
  return (await db.vehicles.toArray()).filter((v) => !v.deleted_at);
}

export async function getVehicle(vehicleId) {
  const v = await db.vehicles.get(vehicleId);
  return v && !v.deleted_at ? v : null;
}
